import React from 'react';
import { Wand2, Brain, Network, BadgeCheck, ChevronRight, Database, Activity, HeartPulse } from 'lucide-react';
import Section, { SectionTitle, SectionSubtitle } from '../components/ui/Section';
import Card, { CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import HeroSection from '../components/ui/HeroSection';
import HeroCard from '../components/ui/HeroCard';
import TechnologyDiagram from './TechnologyDiagram';

const TechnologyPage = () => {
  return (
    <div id="top" className="font-sans text-black">
      {/* Hero Section */}
      <HeroSection
        title="The Large Biosignal Model"
        description="Our groundbreaking AI technology interprets the complex signals generated by the human body, unlocking a new era of proactive, precise healthcare diagnostics."
        buttonText="Learn More"
        buttonLink="#how-it-works"
        buttonVariant="primary"
        buttonScrollTo="how-it-works"
        centered={false}
        paddingY="py-32"
      >
        <HeroCard 
          icon={<Brain className="h-20 w-20 text-primary" />}
          title="Understanding Biosignals"
          description="Just as Large Language Models revolutionized natural language understanding, our LBM is transforming how we interpret the body's natural signals."
        />
      </HeroSection>

      {/* How it Works Section */}
      <Section id="how-it-works">
        <SectionTitle>How the Large Biosignal Model Works</SectionTitle>
        <SectionSubtitle>
          Our breakthrough neural network architecture is specifically designed to decode the complex signals
          produced by the human body, even in noisy real-world environments.
        </SectionSubtitle>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="space-y-6">
              <div className="bg-accent bg-opacity-20 p-6 rounded-xl">
                <h3 className="font-semibold text-primary text-xl mb-3">The Challenge of Biosignals</h3>
                <p className="text-gray-700">
                  Pathophysiological processes produce unique signal patterns that carry critical diagnostic information,
                  but these signals are often buried under noise, making them difficult to interpret.
                </p>
                <p className="text-gray-700 mt-2">
                  Traditional AI models struggle with this complexity because biosignals don't fit neatly into structured,
                  noise-free formats like text or images.
                </p>
              </div>

              <div className="bg-accent bg-opacity-20 p-6 rounded-xl">
                <h3 className="font-semibold text-primary text-xl mb-3">The Decomposer Network</h3>
                <p className="text-gray-700">
                  Unlike conventional deep learning approaches that rely on fixed tokenization (which works for text but not for
                  continuous, complex biosignals), our decomposer network learns its own structure.
                </p>
                <p className="text-gray-700 mt-2">
                  This makes it uniquely capable of separating, identifying, and enhancing key diagnostic components of biosignals,
                  extracting clear and actionable biomarkers from raw signal data.
                </p>
              </div>
            </div>
          </div>

          <TechnologyDiagram />
        </div>
      </Section>

      {/* Key Advantages Section */}
      <Section background="light">
        <SectionTitle>Key Advantages of the Large Biosignal Model</SectionTitle>
        
        <div className="grid md:grid-cols-3 gap-8 mt-12">
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center mb-4">
                <Network className="h-6 w-6 text-primary" />
              </div>
              <CardTitle>Generalization Across Applications</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                Unlike application-specific AI, our LBM works across different biosignal types and medical conditions,
                providing a unified foundation for multiple diagnostic tools.
              </p>
            </CardContent>
          </Card>
          
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center mb-4">
                <BadgeCheck className="h-6 w-6 text-primary" />
              </div>
              <CardTitle>Superior Noise Handling</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                The LBM can filter out background noise and isolate relevant biomarkers, making it
                particularly effective in real-world environments like home monitoring or busy medical facilities.
              </p>
            </CardContent>
          </Card>
          
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center mb-4">
                <Database className="h-6 w-6 text-primary" />
              </div>
              <CardTitle>Unparalleled Dataset</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                Trained on a proprietary 2TB dataset—orders of magnitude larger than public datasets—our
                LBM has exceptional capabilities in biomarker extraction and disease correlation.
              </p>
            </CardContent>
          </Card>
        </div>
      </Section>

      {/* Model Evolution Section */}
      <Section>
        <SectionTitle>Model Evolution Roadmap</SectionTitle>
        <SectionSubtitle>
          Our LBM is on a rapid development trajectory, with clear milestones for scaling and enhancing its capabilities.
        </SectionSubtitle>

        <div className="relative py-8 mt-12">
          <div className="absolute left-4 md:left-1/2 h-full w-1 bg-lightgray"></div>
          
          <div className="relative z-10">
            <div className="flex flex-col md:flex-row md:items-center mb-12">
              <div className="md:w-1/2 mb-4 md:mb-0 md:pr-12 md:text-right">
                <h4 className="font-semibold text-primary mb-1">Today: LBM v1 (GPT-1 scale)</h4>
                <p className="text-gray-700">Proof of Concept Demonstrated</p>
              </div>
              <div className="flex items-center md:justify-center">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                  <div className="w-3 h-3 bg-white rounded-full"></div>
                </div>
              </div>
              <div className="md:w-1/2 md:pl-12 ml-10 md:ml-0">
                <p className="text-gray-700">
                  Extracts biomarkers from signals and correlates with disease states.
                  Already achieving 90% sensitivity in respiratory flare-up detection.
                </p>
              </div>
            </div>
            
            <div className="flex flex-col md:flex-row md:items-center mb-12">
              <div className="md:w-1/2 mb-4 md:mb-0 md:pr-12 md:text-right">
                <h4 className="font-semibold text-primary mb-1">Q2 2025: LBM v2 (GPT-2 scale)</h4>
                <p className="text-gray-700">Advanced Biomarker Separation</p>
              </div>
              <div className="flex items-center md:justify-center">
                <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                  <div className="w-3 h-3 bg-primary rounded-full"></div>
                </div>
              </div>
              <div className="md:w-1/2 md:pl-12 ml-10 md:ml-0">
                <p className="text-gray-700">
                  Enhanced ability to separate biomarkers from noise markers.
                  Improved specificity and reduced false positive rates.
                </p>
              </div>
            </div>
            
            <div className="flex flex-col md:flex-row md:items-center mb-12">
              <div className="md:w-1/2 mb-4 md:mb-0 md:pr-12 md:text-right">
                <h4 className="font-semibold text-primary mb-1">Q4 2025: LBM v3 (GPT-3 scale)</h4>
                <p className="text-gray-700">Cross-Application Generalization</p>
              </div>
              <div className="flex items-center md:justify-center">
                <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                  <div className="w-3 h-3 bg-primary rounded-full"></div>
                </div>
              </div>
              <div className="md:w-1/2 md:pl-12 ml-10 md:ml-0">
                <p className="text-gray-700">
                  Demonstrate system generalizability across multiple biosignal types
                  and clinical applications. Significant scale increase.
                </p>
              </div>
            </div>
            
            <div className="flex flex-col md:flex-row md:items-center">
              <div className="md:w-1/2 mb-4 md:mb-0 md:pr-12 md:text-right">
                <h4 className="font-semibold text-primary mb-1">2026: LBM v4 (GPT-4 scale)</h4>
                <p className="text-gray-700">Industry Standard Establishment</p>
              </div>
              <div className="flex items-center md:justify-center">
                <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                  <div className="w-3 h-3 bg-primary rounded-full"></div>
                </div>
              </div>
              <div className="md:w-1/2 md:pl-12 ml-10 md:ml-0">
                <p className="text-gray-700">
                  Position Senti's LBM as the industry standard in healthcare AI.
                  Massive scale increase with transformer-based architecture.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Applications Section */}
      <Section background="primary">
        <div className="text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Transform Your Medical Device with LBM</h2>
          <p className="text-xl text-white md:opacity-90 max-w-3xl mx-auto mb-10">
            Our LBM technology is available as a service, ready to power your next generation of medical devices.
          </p>
          <Button to="/partners" variant="white" size="lg" icon={<ChevronRight />} scrollTo="top">
            Partner With Us
          </Button>
        </div>
      </Section>

      {/* Data Advantage Section */}
      <Section>
        <SectionTitle>The Data Advantage</SectionTitle>
        
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-lg text-gray-700 mb-6">
              Our LBM is trained on a proprietary dataset of over 2TB of biosignal data—an unprecedented scale
              in the field of biosignals.
            </p>
            
            <p className="text-lg text-gray-700 mb-6">
              For context:
            </p>
            
            <div className="space-y-6">
              <div className="flex items-center">
                <div className="w-24 h-4 bg-secondary rounded-md"></div>
                <div className="ml-4">
                  <p className="font-medium text-primary">Largest public ECG dataset: 3GB</p>
                </div>
              </div>
              
              <div className="flex items-center">
                <div className="w-32 h-4 bg-secondary rounded-md"></div>
                <div className="ml-4">
                  <p className="font-medium text-primary">Largest public EEG dataset: 8.1GB</p>
                </div>
              </div>
              
              <div className="flex items-center">
                <div className="w-full h-4 bg-primary rounded-md"></div>
                <div className="ml-4">
                  <p className="font-medium text-primary">Senti proprietary dataset: 2TB</p>
                </div>
              </div>
            </div>
            
            <div className="mt-8">
              <p className="text-lg text-gray-700">
                This data advantage creates a significant barrier to entry for competitors, as training a similar
                model would require years of data collection and annotation.
              </p>
            </div>
          </div>
          
          <div className="bg-lightgray p-8 rounded-xl flex flex-col items-center">
            <Activity className="h-16 w-16 text-primary mb-6" />
            <h3 className="text-xl font-semibold text-primary mb-4 text-center">Current Clinical Collection</h3>
            <div className="space-y-4 w-full">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Respiratory Signals</span>
                  <span className="font-semibold">200+ patients</span>
                </div>
                <div className="w-full bg-gray-300 rounded-full h-2">
                  <div className="bg-primary h-2 rounded-full" style={{ width: '70%' }}></div>
                </div>
              </div>
              
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Fetal Movement</span>
                  <span className="font-semibold">100+ pregnancies</span>
                </div>
                <div className="w-full bg-gray-300 rounded-full h-2">
                  <div className="bg-primary h-2 rounded-full" style={{ width: '40%' }}></div>
                </div>
              </div>
              
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Bioimpedance Signals</span>
                  <span className="font-semibold">75+ patients</span>
                </div>
                <div className="w-full bg-gray-300 rounded-full h-2">
                  <div className="bg-primary h-2 rounded-full" style={{ width: '30%' }}></div>
                </div>
              </div>
            </div>
            
            <div className="mt-8 text-center">
              <p className="text-gray-700 italic">
                Our data collection is ongoing, with continuous model improvements as our dataset expands.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* CTA Section */}
      <Section background="accent" paddingY="py-16">
        <div className="flex flex-col md:flex-row items-center justify-between">
          <div className="md:w-2/3 mb-8 md:mb-0">
            <h2 className="text-3xl font-bold text-primary mb-4">Ready to Transform Healthcare?</h2>
            <p className="text-xl text-gray-700">
              Whether you're an investor, partner, or medical professional, join us in building the future of proactive healthcare.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
            <Button to="/investors" variant="primary" size="lg" scrollTo="top">For Investors</Button>
            <Button to="/partners" variant="secondary" size="lg" scrollTo="top">For Partners</Button>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default TechnologyPage;